import React, { useState, useEffect } from 'react';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import './DentalChart.css';
import { useParams } from 'react-router-dom';
import { FaPlus, FaTrash } from 'react-icons/fa';
import DataTable from 'react-data-table-component';

const DentalChart = () => {
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [activeContent, setActiveContent] = useState('default');
    const [selectedTooth, setSelectedTooth] = useState(null);
    const [selectedPart, setSelectedPart] = useState(null);
    const [treatments, setTreatments] = useState([]);
    const [selectedProcedure, setSelectedProcedure] = useState('');
    const [toothProcedures, setToothProcedures] = useState({});
    const [savedTreatments, setSavedTreatments] = useState([]);
    const [treatmentDate, setTreatmentDate] = useState('');
    const { id: patientId } = useParams();
    const [showTreatmentModal, setShowTreatmentModal] = useState(false);
    const [treatmentsForPart, setTreatmentsForPart] = useState([]);
    const [showTreatmentListModal, setShowTreatmentListModal] = useState(false);
    const [showProcedureForm, setShowProcedureForm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [supernumeraryTeeth, setSupernumeraryTeeth] = useState([]);
    const [newSuperTooth, setNewSuperTooth] = useState('');
    const [showSuperModal, setShowSuperModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    useEffect(() => {
        const fetchTreatments = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/treatments');
                if (!response.ok) throw new Error('Failed to fetch treatments');
                const data = await response.json();
                setTreatments(data.filter((t) => !t.isArchived));
            } catch (error) {
                console.error('Error fetching treatments:', error);
            }
        };

        fetchTreatments();
    }, []);


    // Handle tooth and part click
    const handleToothClick = (toothNumber, part) => {
        const key = `${toothNumber}-${part}`;
        const matchedTreatments = savedTreatments.filter(t => t.toothPart === key);

        if (!isEditMode && matchedTreatments.length === 0) return; // Prevent interaction if no treatments exist

        setSelectedTooth(toothNumber);
        setSelectedPart(part);

        if (matchedTreatments.length > 0) {
            setTreatmentsForPart(matchedTreatments);
            setShowTreatmentListModal(true);
        } else {
            setSelectedProcedure('');
            setTreatmentDate('');
            setShowTreatmentModal(true);
        }
    };

    // Handle procedure selection
    const handleProcedureSelect = (procedureId) => {
        setSelectedProcedure(procedureId);
    };

    // Save selected treatments
    const handleSaveTreatments = () => {
        if (!selectedProcedure) {
            alert('Please select a procedure first.');
            return;
        }

        if (!treatmentDate) {
            alert('Please select a treatment date first.');
            return;
        }

        const key = `${selectedTooth}-${selectedPart}`;
        const currentProcedures = toothProcedures[key] || [];

        const updatedProcedures = {
            ...toothProcedures,
            [key]: [...currentProcedures, selectedProcedure], // Append the selected procedure
        };

        setToothProcedures(updatedProcedures);

        const treatment = treatments.find(t => t._id === selectedProcedure);

        const newTreatment = {
            toothPart: key,
            procedureId: selectedProcedure,
            treatmentName: treatment ? treatment.Treatmentname : 'Unknown',
            treatmentDate: treatmentDate,
        };

        setSavedTreatments((prevSavedTreatments) => [...prevSavedTreatments, newTreatment]); // Add new treatment to saved list
        setSelectedProcedure(''); // Clear selected procedure after saving
        setTreatmentDate(''); // Clear treatment date after saving
    };

    const getBaseToothNumberForSupernumerary = (superNumber) => {
        // FDI-based mapping from 51–82 to 1–32
        const mapping = {
            51: 1, 52: 2, 53: 3, 54: 4, 55: 5, 56: 6, 57: 7, 58: 8,
            59: 9, 60: 10, 61: 11, 62: 12, 63: 13, 64: 14, 65: 15, 66: 16,
            71: 17, 72: 18, 73: 19, 74: 20, 75: 21, 76: 22, 77: 23, 78: 24,
            79: 25, 80: 26, 81: 27, 82: 32
        };
        return mapping[superNumber] || 1; // fallback to 1 if not found
    };


    const handleSaveChartToDatabase = async () => {
        if (!patientId) {
            alert('Missing patient ID. Cannot save chart.');
            return;
        }

        if (savedTreatments.length === 0) {
            alert('No treatments to save.');
            return;
        }

        const chartData = {
            patientId: patientId,
            treatments: savedTreatments,
            supernumeraryTeeth: supernumeraryTeeth,
        };

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/save-chart', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(chartData),
            });

            const data = await response.json();

            if (!response.ok) {
                console.error('Server error:', data);
                alert(data.error || 'Something went wrong saving the chart.');
                return;
            }

            alert('Dental chart saved successfully!');
        } catch (error) {
            console.error('Error saving dental chart:', error);
            alert('Failed to save dental chart. Please try again.');
        }
    };

    useEffect(() => {
        const fetchDentalChart = async () => {
            try {
                const response = await fetch(`https://api.hgcdental.clinic/api/dental-chart/${patientId}`);
                if (!response.ok) throw new Error('Chart not found');
                const chart = await response.json();
                setSavedTreatments(chart.treatments || []);
                setSupernumeraryTeeth(chart.supernumeraryTeeth || []);
            } catch (error) {
                console.error('Error loading dental chart:', error);
            }
        };

        if (patientId) {
            fetchDentalChart();
        }
    }, [patientId]);

    const handleDisableTooth = (toothNumber) => {
        const parts = ['top', 'left', 'right', 'bottom', 'center'];

        const newTreatments = parts.map(part => ({
            toothPart: `${toothNumber}-${part}`,
            procedureId: null, // or use a predefined ID if you have one
            treatmentName: 'DISABLED TOOTH',
            treatmentDate: new Date().toISOString(),
        }));

        setSavedTreatments(prev => [...prev, ...newTreatments]);
    };

    // Define teeth
    const upperTeeth = Array.from({ length: 16 }, (_, i) => i + 1);
    const lowerTeeth = Array.from({ length: 16 }, (_, i) => 32 - i);

    const toothImages = {
        1: '/teeth/tooth1.png',
        2: '/teeth/tooth2.png',
        3: '/teeth/tooth3.png',
        4: '/teeth/tooth4.png',
        5: '/teeth/tooth5.png',
        6: '/teeth/tooth6.png',
        7: '/teeth/tooth7.png',
        8: '/teeth/tooth8.png',
        9: '/teeth/tooth9.png',
        10: '/teeth/tooth10.png',
        11: '/teeth/tooth11.png',
        12: '/teeth/tooth12.png',
        13: '/teeth/tooth13.png',
        14: '/teeth/tooth14.png',
        15: '/teeth/tooth15.png',
        16: '/teeth/tooth16.png',
        17: '/teeth/tooth17.png',
        18: '/teeth/tooth18.png',
        19: '/teeth/tooth19.png',
        20: '/teeth/tooth20.png',
        21: '/teeth/tooth21.png',
        22: '/teeth/tooth22.png',
        23: '/teeth/tooth23.png',
        24: '/teeth/tooth24.png',
        25: '/teeth/tooth25.png',
        26: '/teeth/tooth26.png',
        27: '/teeth/tooth27.png',
        28: '/teeth/tooth28.png',
        29: '/teeth/tooth29.png',
        30: '/teeth/tooth30.png',
        31: '/teeth/tooth31.png',
        32: '/teeth/tooth32.png',
    };

    const handleDeleteTreatment = async (treatmentId) => {
        if (!window.confirm("Are you sure you want to delete this treatment?")) return;

        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/dental-chart/${patientId}/treatment/${treatmentId}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error('Failed to delete treatment');
            }

            // Remove from frontend state after successful deletion
            setSavedTreatments(prev => prev.filter(t => t._id !== treatmentId));
            alert("Treatment deleted successfully!");
        } catch (error) {
            console.error('Error deleting treatment:', error);
            alert('Failed to delete treatment. Please try again.');
        }
    };

    const columns = [
        { name: 'Tooth-Part', selector: (row) => row.toothPart, sortable: true },
        { name: 'Treatment Name', selector: (row) => row.treatmentName, sortable: true },
        {
            name: 'Treatment Date',
            selector: (row) => new Date(row.treatmentDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            }),
            sortable: true,
        },
        ...(isEditMode ? [ // Only add this column when editing is enabled
            {
                name: 'Actions',
                cell: (row) => (
                    <button
                        className="delete-button"
                        onClick={() => handleDeleteTreatment(row._id)}
                        style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        <FaTrash size={18} color="red" />
                    </button>
                ),
            }
        ] : []), // When isEditMode is false, don't add the column
    ];

    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff',
                fontSize: '18px',
            },
        },
    };

    return (
        // <div className={`dental-chart-container ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
        <div className="dental-chart-container">
            {/* <InternalHeader moduleName="DentalChart" toggleSidebar={toggleSidebar} /> */}
            <div className="dental-chart-content">
                {/* <Sidebar
                    isSidebarVisible={isSidebarVisible}
                    setActiveContent={setActiveContent}
                /> */}
                <div className="tooth-chart">
                    {isEditMode && (
                        <button
                            className="create-button-table"
                            onClick={() => setEditMode(!editMode)}
                        >
                            {editMode ? 'Exit Disable Teeth' : 'Disable Teeth'}
                        </button>
                    )}
                    {isEditMode && (
                        <button
                            className="create-button-table"
                            onClick={() => setShowSuperModal(true)}
                        >
                            Add Supernumerary Tooth
                        </button>
                    )}
                    {isEditMode && (
                        <div className="modal-divider-table" />
                    )}
                    <h2>Select a Tooth and Part</h2>
                    <div className="teeth-grid">
                        {/* Upper Teeth */}
                        <div className="teeth-row">
                            {upperTeeth.map((toothNumber) => (
                                <div
                                    className="tooth-container"
                                    key={toothNumber}
                                    onClick={() => {
                                        if (editMode) {
                                            handleDisableTooth(toothNumber);
                                        }
                                    }}
                                    style={{ cursor: editMode ? 'not-allowed' : 'default' }}
                                >
                                    <div className="tooth-image-container">
                                        <img src={toothImages[toothNumber]} alt={`Tooth ${toothNumber}`} className="tooth-image" />
                                    </div>
                                    <div className="tooth-number">{toothNumber}</div>
                                    <div className="tooth">
                                        {['top', 'left', 'right', 'bottom', 'center'].map((part) => {
                                            const key = `${toothNumber}-${part}`;
                                            const partTreatments = savedTreatments.filter(t => t.toothPart === key);
                                            const isDisabled = partTreatments.some(t => t.treatmentName === 'DISABLED TOOTH');
                                            const isTreated = partTreatments.length > 0;

                                            return (
                                                <button
                                                    key={part}
                                                    className={`part ${part} 
                ${isDisabled ? 'disabled-tooth' : isTreated ? 'treated' : ''} 
                ${selectedTooth === toothNumber && selectedPart === part ? 'selected' : ''}`}
                                                    title={partTreatments.map(t => t.treatmentName).join(', ')}
                                                    onClick={() => {
                                                        if (editMode) return;
                                                        handleToothClick(toothNumber, part);
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Lower Teeth */}
                        <div className="teeth-row">
                            {lowerTeeth.map((toothNumber) => (
                                <div
                                    className="tooth-container"
                                    key={toothNumber}
                                    onClick={() => {
                                        if (editMode) {
                                            handleDisableTooth(toothNumber);
                                        }
                                    }}
                                    style={{ cursor: editMode ? 'not-allowed' : 'default' }}
                                >
                                    <div className="tooth">
                                        {['top', 'left', 'right', 'bottom', 'center'].map((part) => {
                                            const key = `${toothNumber}-${part}`;
                                            const partTreatments = savedTreatments.filter(t => t.toothPart === key);
                                            const isDisabled = partTreatments.some(t => t.treatmentName === 'DISABLED TOOTH');
                                            const isTreated = partTreatments.length > 0;

                                            return (
                                                <button
                                                    key={part}
                                                    className={`part ${part} 
                ${isDisabled ? 'disabled-tooth' : isTreated ? 'treated' : ''} 
                ${selectedTooth === toothNumber && selectedPart === part ? 'selected' : ''}`}
                                                    title={partTreatments.map(t => t.treatmentName).join(', ')}
                                                    onClick={() => {
                                                        if (editMode) return;
                                                        handleToothClick(toothNumber, part);
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                    <div className="tooth-number">{toothNumber}</div>
                                    <img src={toothImages[toothNumber]} alt={`Tooth ${toothNumber}`} className="tooth-image" />
                                </div>
                            ))}
                        </div>
                    </div>
                    {supernumeraryTeeth.length > 0 && (
                        <div className="supernumerary-teeth">
                            <h3>Supernumerary Teeth</h3>
                            <div className="teeth-row">
                                {supernumeraryTeeth.map((tooth) => (
                                    <div
                                        className="tooth-container"
                                        key={tooth.number}
                                        onClick={() => {
                                            if (editMode) {
                                                handleDisableTooth(tooth.number);
                                            }
                                        }}
                                        style={{ cursor: editMode ? 'not-allowed' : 'default' }}
                                    >
                                        <div className="tooth">
                                            {['top', 'left', 'right', 'bottom', 'center'].map((part) => {
                                                const key = `${tooth.number}-${part}`;
                                                const partTreatments = savedTreatments.filter(t => t.toothPart === key);
                                                const isDisabled = partTreatments.some(t => t.treatmentName === 'DISABLED TOOTH');
                                                const isTreated = partTreatments.length > 0;

                                                return (
                                                    <button
                                                        key={part}
                                                        className={`part ${part} 
                                        ${isDisabled ? 'disabled-tooth' : isTreated ? 'treated' : ''} 
                                        ${selectedTooth === tooth.number && selectedPart === part ? 'selected' : ''}`}
                                                        title={partTreatments.map(t => t.treatmentName).join(', ')}
                                                        onClick={() => {
                                                            if (editMode) return;
                                                            handleToothClick(tooth.number, part);
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <div className="tooth-number">{tooth.number}</div>
                                        <img
                                            src={`/teeth/tooth${getBaseToothNumberForSupernumerary(tooth.number)}.png`}
                                            alt={`Tooth ${tooth.number}`}
                                            className="tooth-image"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Saved Treatments Table */}
                    <div className="saved-treatments">
                        <h3>Treatment History</h3>
                        <div className="modal-divider-table" />
                        <div className="table-form-table">
                            <DataTable
                                columns={columns}
                                data={savedTreatments.filter(treatment => treatment.treatmentName !== 'DISABLED TOOTH')} // Hide disabled treatments
                                highlightOnHover
                                customStyles={customStyles}
                            />
                        </div>
                    </div>

                    {showTreatmentModal && (
                        <div className="modal-overlay" onClick={() => setShowTreatmentModal(false)}>
                            <div className="modal treatment-modal" onClick={(e) => e.stopPropagation()}>
                                <h3>Select Procedure for Tooth {selectedTooth}, Part: {selectedPart}</h3>
                                <div className="modal-divider" />
                                <label>Select Procedure</label>
                                <select value={selectedProcedure} onChange={(e) => handleProcedureSelect(e.target.value)}>
                                    <option value="">Select Procedure</option>
                                    {treatments.map((treatment) => (
                                        <option key={treatment._id} value={treatment._id}>
                                            {treatment.Treatmentname} - ₱{treatment.Cost}
                                        </option>
                                    ))}
                                </select>

                                <label htmlFor="treatmentdate">Treatment Date</label>
                                <input
                                    type="date"
                                    id="treatmentdate"
                                    value={treatmentDate}
                                    onChange={(e) => setTreatmentDate(e.target.value)}
                                    className="modal-input"
                                />
                                <div className="modal-divider-2" />
                                <button
                                    className="create-button-table-1"
                                    onClick={() => {
                                        handleSaveTreatments();
                                        setShowTreatmentModal(false); // close modal after saving
                                    }}
                                >
                                    Save Treatment
                                </button>
                            </div>
                        </div>
                    )}

                    {showTreatmentListModal && (
                        <div className="modal-overlay" onClick={() => {
                            setShowTreatmentListModal(false);
                            setShowProcedureForm(false); // Reset when closing
                        }}>
                            <div className="modal treatment-modal" onClick={(e) => e.stopPropagation()}>
                                <h3>Treatments for Tooth {selectedTooth}, Part: {selectedPart}</h3>
                                <div className="modal-divider" />
                                <ul className="treatment-list">
                                    {treatmentsForPart
                                        .filter(t => t.treatmentName !== 'DISABLED TOOTH') // Hide disabled treatments
                                        .map((t, index) => (
                                            <li key={index}>
                                                <strong>{t.treatmentName}</strong> <br />
                                                <span>{new Date(t.treatmentDate).toLocaleDateString()}</span>
                                            </li>
                                        ))}
                                </ul>
                                <div className="modal-divider" />
                                <button
                                    className="create-button-table"
                                    onClick={() => {
                                        setShowTreatmentListModal(false);
                                        setShowProcedureForm(false);
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
                    {showSuperModal && (
                        <div className="modal-overlay" onClick={() => setShowSuperModal(false)}>
                            <div className="modal treatment-modal" onClick={(e) => e.stopPropagation()}>
                                <h3>Add Supernumerary Tooth</h3>
                                <div className="modal-divider" />

                                <input
                                    type="number"
                                    min="51"
                                    max="82"
                                    value={newSuperTooth}
                                    onChange={(e) => setNewSuperTooth(Number(e.target.value))}
                                    placeholder="Enter Tooth # (51–82)"
                                    className="modal-input"
                                />

                                <div className="modal-divider-2" />

                                <button
                                    className="create-button-table-1"
                                    onClick={() => {
                                        if (!newSuperTooth || newSuperTooth < 51 || newSuperTooth > 82) {
                                            alert('Please enter a valid tooth number between 51 and 82.');
                                            return;
                                        }

                                        const exists = supernumeraryTeeth.some(t => t.number === newSuperTooth);
                                        if (exists) {
                                            alert('This supernumerary tooth already exists.');
                                            return;
                                        }

                                        setSupernumeraryTeeth(prev => [...prev, { number: newSuperTooth }]);
                                        setNewSuperTooth('');
                                        setShowSuperModal(false); // Close modal after saving
                                    }}
                                >
                                    Save Supernumerary Tooth
                                </button>

                                <button
                                    className="create-button-table"
                                    style={{ marginTop: '10px' }}
                                    onClick={() => setShowSuperModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DentalChart;